/**
 * PostPreviewModal — 저장 전 본문 미리보기 모달
 *
 * 작성/수정 화면에서 [미리보기] 버튼을 누르면 본문이 어떻게 렌더되는지 확인할 수 있다.
 * 위키 보드에서는 우측에 TOC 사이드바도 함께 표시 — 실제 상세보기와 동일 구조.
 *
 * 렌더된 heading 요소에 TOC item.id 와 매칭되는 DOM id 를 주입해 클릭 스크롤이 동작하도록 한다.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { ListTree, HelpCircle, Eye, Hash } from 'lucide-react';
import BoardRichEditor from './BoardRichEditor';
import { extractTocFromJson, TocItem } from './tocUtils';
import TocHelpModal from './TocHelpModal';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 페이지/게시글 제목 */
  title: string;
  /** TipTap JSON 본문 */
  editorJson: any;
  /** 태그 (옵션) */
  tags?: string[];
  /** 위키 보드 여부 — TOC 사이드바 표시 분기 */
  isWiki?: boolean;
  /** 멘션된 위키 페이지 매핑 (위키일 때만) */
  wikiPages?: any;
}

export const PostPreviewModal: React.FC<Props> = ({
  open, onClose, title, editorJson, tags, isWiki, wikiPages,
}) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const tocItems = useMemo<TocItem[]>(() => {
    return extractTocFromJson(editorJson);
  }, [editorJson]);

  // 렌더된 heading 요소에 TOC item.id 주입 — 클릭 스크롤 활성화
  useEffect(() => {
    if (!open) return;
    if (!bodyRef.current) return;
    if (tocItems.length === 0) return;

    // BoardRichEditor 가 마운트되어 헤딩이 DOM 에 그려질 시간을 잠시 기다린다
    const t = setTimeout(() => {
      const root = bodyRef.current;
      if (!root) return;
      const headings = root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6');
      headings.forEach((el, idx) => {
        if (idx < tocItems.length) {
          el.id = tocItems[idx].id;
          el.style.scrollMarginTop = '12px';
        }
      });
    }, 60);
    return () => clearTimeout(t);
  }, [open, editorJson, tocItems]);

  const handleTocClick = (id: string) => {
    const root = bodyRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const showTocSidebar = isWiki && tocItems.length > 0;
  const isEmpty = !editorJson || !title.trim();

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[1100px] w-[95vw] h-[88vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-5 py-3 border-b border-border bg-muted/30 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-body font-semibold text-foreground">
            <Eye className="size-[18px] text-primary" />
            <Trans>Preview</Trans>
            <span className="inline-flex items-center px-1.5 rounded-full bg-primary/10 text-primary text-meta-sm font-semibold">
              <Trans>Before saving</Trans>
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>Preview how your content will look after saving. Wiki boards show the table of contents on the right.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* ── 좌측 본문 ── */}
          <div className="flex-1 min-w-0 overflow-y-auto px-6 py-6" ref={bodyRef}>
            {/* 제목 */}
            <h1 className="text-display-lg font-bold tracking-tight text-foreground mb-2">
              {title.trim() || <span className="text-muted-foreground/60">{t`(no title)`}</span>}
            </h1>

            {/* 태그 */}
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-4">
                {tags.map(t => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-meta-sm font-medium"
                  >
                    <Hash className="size-2.5 opacity-70" />
                    {t}
                  </span>
                ))}
              </div>
            )}

            <hr className="border-border mb-4" />

            {/* 본문 — readonly */}
            {isEmpty ? (
              <p className="text-muted-foreground text-body py-12 text-center">
                <Trans>No content to preview.</Trans>
              </p>
            ) : (
              <div className="text-body-lg text-foreground leading-[1.75]">
                <BoardRichEditor
                  initialContent={editorJson}
                  editable={false}
                  minHeight={0}
                  wikiPages={wikiPages}
                />
              </div>
            )}
          </div>

          {/* ── 우측 TOC 사이드 (위키, 헤딩 있을 때만) ── */}
          {showTocSidebar && (
            <aside className="hidden md:block w-[220px] shrink-0 border-l border-border bg-muted/10 overflow-y-auto">
              <div className="p-3 sticky top-0">
                <div className="flex items-center gap-1.5 mb-2">
                  <ListTree className="size-3.5 text-muted-foreground" />
                  <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    <Trans>TOC</Trans>
                  </span>
                  <button
                    type="button"
                    onClick={() => setHelpOpen(true)}
                    className="ml-auto inline-flex items-center justify-center size-5 rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors cursor-pointer"
                    title={t`Table of contents help`}
                    aria-label={t`Table of contents help`}
                  >
                    <HelpCircle className="size-3.5" />
                  </button>
                </div>
                <nav className="space-y-0.5">
                  {tocItems.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className="w-full text-left text-meta text-muted-foreground hover:text-foreground truncate transition-colors cursor-pointer py-0.5"
                      style={{ paddingLeft: (item.level - 1) * 12 }}
                      onClick={() => handleTocClick(item.id)}
                      title={item.text}
                    >
                      {item.text}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          {/* 위키 보드이지만 헤딩이 0개 — 안내만 표시 */}
          {isWiki && tocItems.length === 0 && (
            <aside className="hidden md:block w-[220px] shrink-0 border-l border-border bg-muted/10 overflow-y-auto">
              <div className="p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <ListTree className="size-3.5 text-muted-foreground" />
                  <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    <Trans>TOC</Trans>
                  </span>
                  <button
                    type="button"
                    onClick={() => setHelpOpen(true)}
                    className="ml-auto inline-flex items-center justify-center size-5 rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors cursor-pointer"
                    title={t`Table of contents help`}
                    aria-label={t`Table of contents help`}
                  >
                    <HelpCircle className="size-3.5" />
                  </button>
                </div>
                <p className="text-meta-sm text-muted-foreground leading-relaxed">
                  <Trans>Add headings (H1/H2/H3) to show a table of contents here.</Trans>
                  <button
                    type="button"
                    onClick={() => setHelpOpen(true)}
                    className="block mt-2 text-primary hover:underline cursor-pointer"
                  >
                    <Trans>See how to write headings</Trans> →
                  </button>
                </p>
              </div>
            </aside>
          )}
        </div>

        <TocHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};

export default PostPreviewModal;
